/**
 * A blocking promise queue is maintaining a list operations and run each one in sequence.
 * It will make sure the promise from any prior invoked operation is either resolved or rejected, before invoking any new operations.
 * The operations will be invoked in the sequence they are added to the queue.
 *
 * Additionally supports cancellable read operations via enqueueRead.
 * When a read operation with the same key is enqueued, any pending (not yet started) operation
 * with the same key will be cancelled and replaced with the new one.
 */
type Operation<T> = () => Promise<T>;

type PendingTask = {
  (): Promise<void>;
  key?: string;
  background?: boolean;
};

export class BlockingPromiseQueue {
  private _pendingTasks: PendingTask[] = [];
  private _currentlyRunningTask: null | Promise<void> = null;
  private _cancellationTokens: Map<string, AbortController> = new Map();

  get pendingTasks(): PendingTask[] {
    return this._pendingTasks;
  }

  get currentlyRunningTask(): Promise<void> | null {
    return this._currentlyRunningTask;
  }

  /**
   * Adds an operation to the queue.
   * The operation will be invoked as soon as any prior operations has been resolved.
   */
  enqueue = <T>(operation: Operation<T>): Promise<T> => {
    const { promise, executor } = this.getPromiseAndExecutor(operation);
    if (this._currentlyRunningTask === null) {
      const runningTask = executor();
      this._currentlyRunningTask = runningTask;
      runningTask.finally(this.runAllPendingTasks);
    } else {
      this.insertPendingTask(executor);
    }
    return promise;
  };

  /**
   * Adds a background operation (e.g. periodic document polling).
   * Background operations yield to regular ones: any operation enqueued via
   * enqueue/enqueueLatest is inserted ahead of pending background tasks, so
   * user-initiated work never waits behind queued polls. A background task
   * that has already started still runs to completion first.
   */
  enqueueBackground = <T>(operation: Operation<T>): Promise<T> => {
    const { promise, executor } = this.getPromiseAndExecutor(operation);
    executor.background = true;
    if (this._currentlyRunningTask === null) {
      const runningTask = executor();
      this._currentlyRunningTask = runningTask;
      runningTask.finally(this.runAllPendingTasks);
    } else {
      this.insertPendingTask(executor);
    }
    return promise;
  };

  /**
   * Adds a read operation to the queue with optional cancellation support.
   * If an operationKey is provided and a pending task with the same key exists,
   * the pending task will be cancelled and replaced with the new operation.
   * If no operationKey is provided, behaves exactly like enqueue.
   */
  enqueueLatest = <T>(
    operation: Operation<T>,
    operationKey: string,
  ): Promise<T> => {
    // Abort any existing pending operation with the same key
    const existingController = this._cancellationTokens.get(operationKey);
    if (existingController) {
      existingController.abort();
    }

    const controller = new AbortController();
    this._cancellationTokens.set(operationKey, controller);

    // Remove any pending tasks with the same key that haven't started yet
    this._pendingTasks = this._pendingTasks.filter(
      (task) => task.key !== operationKey,
    );

    const { promise, executor } = this.getPromiseAndExecutor(async () => {
      if (controller.signal.aborted) {
        return undefined as T;
      }
      try {
        return await operation();
      } finally {
        this._cancellationTokens.delete(operationKey);
      }
    });

    const keyedExecutor: PendingTask = executor;
    keyedExecutor.key = operationKey;

    if (this._currentlyRunningTask === null) {
      const runningTask = keyedExecutor();
      this._currentlyRunningTask = runningTask;
      runningTask.finally(this.runAllPendingTasks);
    } else {
      this.insertPendingTask(keyedExecutor);
    }

    return promise;
  };

  /**
   * Regular tasks are inserted ahead of any pending background tasks while
   * preserving order within each group; background tasks always append.
   */
  private insertPendingTask = (task: PendingTask) => {
    if (task.background) {
      this._pendingTasks.push(task);
      return;
    }

    const firstBackgroundIndex = this._pendingTasks.findIndex(
      (pendingTask) => pendingTask.background,
    );
    if (firstBackgroundIndex === -1) {
      this._pendingTasks.push(task);
    } else {
      this._pendingTasks.splice(firstBackgroundIndex, 0, task);
    }
  };

  private runAllPendingTasks = async () => {
    const nextTask = this._pendingTasks.shift();
    if (nextTask) {
      try {
        await nextTask();
      } finally {
        await this.runAllPendingTasks();
      }
    }
    this._currentlyRunningTask = null;
  };

  /**
   * Creates a new promise that encapsulates the operation. The operation and the promise
   * are controlled by the executor. Once the executor is invoked, the operation will be
   * invoked and once the operation is resolved so is the promise.
   *
   * The resulting promise will be resolved or rejected with the result from the operation.
   */
  private getPromiseAndExecutor = <T>(
    operation: Operation<T>,
  ): {
    promise: Promise<T>;
    executor: PendingTask;
  } => {
    let resolveIt: ((result: T) => void) | null = null;
    let rejectIt: ((error: unknown) => void) | null = null;
    const promise = new Promise<T>((resolve, reject) => {
      resolveIt = resolve;
      rejectIt = reject;
    });
    const executor: PendingTask = async () => {
      try {
        const result = await operation();
        if (resolveIt) {
          resolveIt(result);
        }
      } catch (error) {
        if (rejectIt) {
          rejectIt(error);
        }
      }
    };
    return {
      promise,
      executor,
    };
  };
}
